import { useCompactorStore } from '../store/compactor'
import { RAW_CACHE } from '../variant'

/**
 * Background image-cache compactor.
 *
 * WHY: the Bluesky CDN serves no CORS headers, so every image the service
 * worker pins lands in Cache Storage as an OPAQUE response. Opaque entries
 * are (a) unreadable to script and (b) quota-padded by the browser —
 * Chrome bills ~7 MB per entry regardless of real size — so a few thousand
 * thumbnails "fill" a quota that should hold hundreds of thousands. That
 * padding, not the actual bytes, is what drives the constant
 * QuotaExceededError failures.
 *
 * WHAT: walk every entry in the image/avatar caches and rewrite it as a
 * readable response at its true size: opaque entries are re-fetched through
 * the worker's CORS /api/img proxy, re-encoded to WebP/JPEG, and stored
 * back under the same URL. NOTHING IS EVER DELETED — an entry whose
 * upstream blob is gone (404) can't be read and is left in place untouched;
 * it keeps rendering from the opaque copy. This preserves the pinned-media
 * guarantee: compaction is lossless in coverage, lossy only in pixels.
 *
 * Escalation: tier 1 (2048px / q0.82) runs first; if storage stays above
 * the high-water mark the pass repeats at tier 2, then 3. Tier is stamped
 * on each rewritten entry (x-bskyhq-c header) so passes never redo work.
 */

const IMAGE_CACHE = 'bskyhq-images-v1'
const AVATAR_CACHE = 'bskyhq-avatars-v1'
/** Skip-markers for entries whose upstream blob is gone — kept in a side
 *  cache (not Dexie: no schema bump, same storage bucket, survives reload). */
const META_CACHE = 'bskyhq-compact-meta-v1'
const MARKER = 'x-bskyhq-c'

const HIGH_WATER = 0.7
const BOOT_DELAY_MS = 15_000
const PERIODIC_MS = 10 * 60_000

const TIERS = [
  { tier: 1, maxDim: 2048, quality: 0.82 },
  { tier: 2, maxDim: 1600, quality: 0.68 },
  { tier: 3, maxDim: 1280, quality: 0.55 },
] as const

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

function proxyUrl(src: string): string {
  return `${API_BASE}/api/img?u=${encodeURIComponent(src)}`
}

async function usageRatio(): Promise<number> {
  try {
    const est = await navigator.storage?.estimate?.()
    if (!est?.quota || est.usage == null) return 0
    return est.usage / est.quota
  } catch {
    return 0
  }
}

/** Re-encode a readable image blob. Returns null when transcoding is
 *  impossible or pointless (animations, vectors, decode failure) — the
 *  caller then stores the original bytes, which still removes padding.
 *  Exported for reuse by the disk archiver. */
export async function reencode(
  blob: Blob,
  maxDim: number,
  quality: number,
): Promise<Blob | null> {
  if (blob.type === 'image/gif' || blob.type === 'image/svg+xml') return null
  let bmp: ImageBitmap
  try {
    bmp = await createImageBitmap(blob)
  } catch {
    return null
  }
  try {
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height))
    const w = Math.max(1, Math.round(bmp.width * scale))
    const h = Math.max(1, Math.round(bmp.height * scale))
    let canvas: OffscreenCanvas | HTMLCanvasElement
    if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(w, h)
    } else {
      canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
    }
    // Both canvas kinds expose an identical 2d API for what we use;
    // cast through HTMLCanvasElement to sidestep TS's union-call limits.
    const ctx = (canvas as HTMLCanvasElement).getContext('2d')
    if (!ctx) return null
    ctx.drawImage(bmp, 0, 0, w, h)
    const encode = async (type: string): Promise<Blob | null> => {
      try {
        if ('convertToBlob' in canvas) return await canvas.convertToBlob({ type, quality })
        return await new Promise<Blob | null>((res) =>
          (canvas as HTMLCanvasElement).toBlob(res, type, quality),
        )
      } catch {
        return null
      }
    }
    // WebP first; Safari may silently encode PNG instead — fall back to
    // JPEG and keep whichever is smallest.
    let out = await encode('image/webp')
    if (!out || out.type !== 'image/webp') {
      const jpg = await encode('image/jpeg')
      if (jpg && (!out || jpg.size < out.size)) out = jpg
    }
    return out
  } finally {
    bmp.close()
  }
}

/** Write a readable, tier-stamped response under `url`. At a hard-full
 *  quota the first put can be rejected — delete-then-retry is safe because
 *  the replacement bytes are already in memory and strictly smaller. */
async function putSafe(cache: Cache, url: string, blob: Blob, tier: number): Promise<boolean> {
  const make = () =>
    new Response(blob, {
      headers: {
        'content-type': blob.type || 'application/octet-stream',
        'cache-control': 'public, max-age=31536000, immutable',
        date: new Date().toUTCString(),
        [MARKER]: String(tier),
      },
    })
  try {
    await cache.put(url, make())
    return true
  } catch {
    try {
      await cache.delete(url)
      await cache.put(url, make())
      return true
    } catch {
      return false
    }
  }
}

interface PassStats {
  rewritten: number
  skipped: number
}

async function compactCache(
  name: 'images' | 'avatars',
  cacheName: string,
  tierSpec: (typeof TIERS)[number],
): Promise<PassStats> {
  const stats: PassStats = { rewritten: 0, skipped: 0 }
  const cache = await caches.open(cacheName)
  const meta = await caches.open(META_CACHE)
  const keys = await cache.keys()
  const store = useCompactorStore.getState()
  store.progress({ phase: name, tier: tierSpec.tier, done: 0, total: keys.length })

  let done = 0
  for (const req of keys) {
    done++
    if (done % 10 === 0 || done === keys.length) {
      useCompactorStore.getState().progress({ phase: name, tier: tierSpec.tier, done, total: keys.length })
    }
    // Pace the walk so decode/encode never competes with the UI. Hidden
    // tabs can run near-flat-out.
    await new Promise((r) => setTimeout(r, document.hidden ? 5 : 40))
    try {
      const resp = await cache.match(req)
      if (!resp) continue
      const curTier = Number(resp.headers.get(MARKER) ?? 0)
      if (curTier >= tierSpec.tier) {
        stats.skipped++
        continue
      }

      let blob: Blob | null = null
      if (resp.type === 'opaque') {
        // Known-gone upstream → the opaque copy is the only copy. Leave it.
        if (await meta.match(req.url)) {
          stats.skipped++
          continue
        }
        try {
          const r = await fetch(proxyUrl(req.url), { mode: 'cors' })
          // 404/410 = blob deleted upstream; 400 = the CDN rejects the URL
          // outright (malformed CID). All permanent — never re-probe.
          if (r.status === 400 || r.status === 404 || r.status === 410) {
            await meta.put(req.url, new Response('', { headers: { 'x-gone': '1' } })).catch(() => {})
            stats.skipped++
            continue
          }
          if (!r.ok) {
            stats.skipped++ // transient (5xx, worker hiccup) — retry next run
            continue
          }
          blob = await r.blob()
        } catch {
          stats.skipped++ // offline — retry next run
          continue
        }
      } else {
        blob = await resp.clone().blob()
      }
      if (!blob || blob.size === 0) {
        stats.skipped++
        continue
      }

      const smaller = await reencode(blob, tierSpec.maxDim, tierSpec.quality)
      // Keep the re-encode only when it meaningfully shrinks; otherwise
      // store the original readable bytes — for ex-opaque entries that
      // alone reclaims the ~7 MB quota padding.
      const finalBlob = smaller && smaller.size < blob.size * 0.95 ? smaller : blob
      if (await putSafe(cache, req.url, finalBlob, tierSpec.tier)) stats.rewritten++
      else stats.skipped++
    } catch {
      stats.skipped++ // per-entry failures never stop the sweep
    }
  }
  return stats
}

let runningLocal = false

export interface CompactOptions {
  /** Manual runs always do a full tier-1 pass even below the high-water mark. */
  manual?: boolean
}

/** Run a full compaction. Resolves when done. Safe to call repeatedly —
 *  concurrent calls (this tab or others, via Web Locks) no-op. */
export async function compactImageCaches(opts: CompactOptions = {}): Promise<void> {
  if (RAW_CACHE) return // raw variant: never touch cached blocks
  if (runningLocal) return
  if (typeof caches === 'undefined') return
  const ratio = await usageRatio()
  if (!opts.manual && ratio < HIGH_WATER) return

  const run = async () => {
    runningLocal = true
    const store = useCompactorStore.getState()
    store.start()
    let freed = 0
    let warning: string | null = null
    try {
      const before = await navigator.storage?.estimate?.()
      for (const tierSpec of TIERS) {
        await compactCache('images', IMAGE_CACHE, tierSpec)
        await compactCache('avatars', AVATAR_CACHE, tierSpec)
        if ((await usageRatio()) < HIGH_WATER) break
        if (tierSpec.tier === 3) {
          warning =
            'storage is still above the safe level after maximum compression — nothing was deleted, but the disk itself may be low on space'
        }
      }
      const after = await navigator.storage?.estimate?.()
      if (before?.usage != null && after?.usage != null) {
        freed = Math.max(0, before.usage - after.usage)
      }
    } finally {
      runningLocal = false
      useCompactorStore.getState().finish(freed, warning)
    }
  }

  // One compactor across all tabs — Web Locks when available.
  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (locks?.request) {
    await locks.request('bskyhq-compactor', { ifAvailable: true }, async (lock) => {
      if (lock) await run()
    })
  } else {
    await run()
  }
}

/** Wire the automatic triggers: delayed boot check, periodic re-check, and
 *  the quota-pressure event fired by the failure log's write path the
 *  moment IndexedDB starts rejecting writes. Returns a cleanup fn. */
export function initAutoCompaction(): () => void {
  const boot = setTimeout(() => void compactImageCaches(), BOOT_DELAY_MS)
  const every = setInterval(() => void compactImageCaches(), PERIODIC_MS)
  const onPressure = () => void compactImageCaches()
  window.addEventListener('bskyhq:quota-pressure', onPressure)
  return () => {
    clearTimeout(boot)
    clearInterval(every)
    window.removeEventListener('bskyhq:quota-pressure', onPressure)
  }
}
